/**
 * Phase 40 — Per-section Telegram alerts panel.
 *
 * Drives the `/api/alerts/pause-config` endpoints. Renders one independent
 * switch per entry in `SECTIONS` below (currently: Candle Breakout, OI
 * Long-Buildup, Live Orders, VWAP Bounce, Setup E V2, Commodities) — add a
 * row there and a matching key in backend `alerts.SECTION_KEYS` to cover a
 * new section; no other wiring needed on either side.
 *
 * Defaults from backend: scan/paper-alert sections paused by default,
 * live-order-style sections (Section 7, Commodities) alerting by default.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bell, BellOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { key: "section5", label: "Section 5", sub: "Candle Breakout · scan & paper" },
  { key: "section6", label: "Section 6", sub: "OI Long-Buildup · scan & paper" },
  { key: "section7", label: "Section 7", sub: "Live Orders · BUY / SELL / chase" },
  { key: "section8", label: "Section 8", sub: "VWAP Bounce · scan & paper" },
  { key: "section9", label: "Section 9", sub: "Setup E V2 · Expiry Momentum" },
  // 2026-09-08 — previously had no switch here at all; it rode the GLOBAL
  // pause (no UI anywhere) shared with Sniper, so it couldn't be enabled
  // independently. Now has its own section key (alerts.SECTION_KEYS) —
  // alert-by-default, same convention as Section 7.
  { key: "commodities", label: "Commodities", sub: "MCX · scan & paper" },
];

const Switch = ({ checked, onChange, testId, ariaLabel }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    onClick={onChange}
    data-testid={testId}
    className="relative inline-flex items-center w-9 h-5 transition-colors duration-200 border"
    style={{
      borderColor: checked ? "var(--bull)" : "var(--border-color)",
      background: checked ? "rgba(0, 200, 120, 0.12)" : "transparent",
    }}
  >
    <span
      className="absolute w-3 h-3 transition-transform duration-200"
      style={{
        background: checked ? "var(--bull)" : "var(--text-muted)",
        left: 3,
        transform: checked ? "translateX(16px)" : "translateX(0)",
      }}
    />
  </button>
);

export const AlertsConfig = () => {
  const [config, setConfig] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/alerts/pause-config`);
      setConfig(r.data?.sections || {});
      setError(null);
    } catch (e) {
      setError(e.message || "load failed");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (section, currentlyPaused) => {
    if (pending) return;
    setPending(true);
    // Optimistic update
    setConfig((c) => ({ ...(c || {}), [section]: !currentlyPaused }));
    try {
      await axios.post(
        `${API}/alerts/pause-config?section=${section}&paused=${!currentlyPaused}`,
      );
      setError(null);
    } catch (e) {
      setError(e.message || "save failed");
      // Revert on failure
      setConfig((c) => ({ ...(c || {}), [section]: currentlyPaused }));
    } finally {
      setPending(false);
    }
  };

  if (!config) {
    return (
      <div
        className="text-[10px] uppercase tracking-wider text-muted-3 font-mono-data"
        data-testid="alerts-config-loading"
      >
        Loading alert config…
      </div>
    );
  }

  // alerting = !paused
  const anyAlerting = SECTIONS.some((s) => !config[s.key]);

  return (
    <section
      className="border border-grid surface p-4"
      data-testid="alerts-config-panel"
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {anyAlerting ? (
            <Bell size={14} className="text-bull" />
          ) : (
            <BellOff size={14} className="text-muted-3" />
          )}
          <h2 className="text-[11px] uppercase tracking-wider font-mono-data font-semibold">
            Telegram Alert Channels
          </h2>
        </div>
        <span className="text-[9px] uppercase tracking-wider text-muted-3 font-mono-data">
          Each section persists independently
        </span>
      </header>
      {error && (
        <div
          className="text-[10px] text-bear mb-2 font-mono-data"
          data-testid="alerts-config-error"
        >
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {SECTIONS.map((s) => {
          const paused = Boolean(config[s.key]);
          const alerting = !paused;
          return (
            <div
              key={s.key}
              className="border border-grid p-3 flex items-start justify-between gap-3"
              data-testid={`alerts-config-card-${s.key}`}
            >
              <div className="min-w-0">
                <div className="text-[11px] font-mono-data font-semibold">
                  {s.label}
                </div>
                <div className="text-[10px] text-muted-3 mt-0.5">{s.sub}</div>
                <div
                  className="text-[10px] mt-1.5 uppercase tracking-wider font-mono-data"
                  style={{ color: alerting ? "var(--bull)" : "var(--text-muted)" }}
                  data-testid={`alerts-config-state-${s.key}`}
                >
                  {alerting ? "Alerting" : "Paused"}
                </div>
              </div>
              <Switch
                checked={alerting}
                onChange={() => toggle(s.key, paused)}
                testId={`alerts-config-toggle-${s.key}`}
                ariaLabel={`${alerting ? "Pause" : "Enable"} ${s.label} alerts`}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AlertsConfig;
